import { FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision";
import {
  silhouetteLayout,
  DEFAULT_LAYOUT,
  type LayoutOptions,
  type Rect,
  type Label,
} from "./silhouette.js";

const WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite";

interface Feed {
  video: HTMLVideoElement;
  work: HTMLCanvasElement; // offscreen frame buffer for masking
  name: string;
}

/**
 * Composites MST3K-style silhouettes (PRD §8) of each participant along the
 * bottom of a transparent overlay canvas; the movie shows through underneath.
 * Segmentation runs on the RECEIVE side (WebRTC carries no alpha) via MediaPipe
 * Selfie Segmentation.
 *
 * Degrades gracefully: if the segmenter can't load (offline, no WASM/GPU), it
 * draws the raw feed into the same layout rect — the silhouette area still shows
 * the person, just without the cutout. The experience is never gated on it.
 *
 * Placement geometry comes from the pure, unit-tested `silhouetteLayout`.
 */
export class SilhouetteCompositor {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly feeds = new Map<string, Feed>();
  private segmenter: ImageSegmenter | null = null;
  private raf = 0;
  private running = false;
  private segFailures = 0;
  /** Set true once segmentation is active; false means fallback raw draw. */
  segmenting = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly layout: LayoutOptions = DEFAULT_LAYOUT,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d canvas context unavailable");
    this.ctx = ctx;
  }

  /** Best-effort segmenter load; safe to call once. Never throws. */
  async init(): Promise<void> {
    try {
      const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
      this.segmenter = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        outputCategoryMask: true,
        outputConfidenceMasks: false,
      });
      this.segmenting = true;
    } catch {
      this.segmenter = null;
      this.segmenting = false; // fallback mode
    }
  }

  addPeer(peerId: string, stream: MediaStream, name = ""): void {
    const prevName = this.feeds.get(peerId)?.name ?? "";
    if (this.feeds.has(peerId)) this.removePeer(peerId);
    if (stream.getVideoTracks().length === 0) return; // audio-only participant
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    void video.play().catch(() => {});
    this.feeds.set(peerId, { video, work: document.createElement("canvas"), name: name || prevName });
  }

  /** Update the display name shown under a peer's silhouette. */
  setName(peerId: string, name: string): void {
    const f = this.feeds.get(peerId);
    if (f) f.name = name;
  }

  removePeer(peerId: string): void {
    const f = this.feeds.get(peerId);
    if (!f) return;
    f.video.srcObject = null;
    this.feeds.delete(peerId);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const tick = () => {
      if (!this.running) return;
      this.draw();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /** Render one frame: clear, frame the group, then draw each silhouette + name. */
  draw(): void {
    const { width, height } = this.canvas;
    this.ctx.clearRect(0, 0, width, height);
    const feeds = [...this.feeds.values()].filter((f) => f.video.readyState >= 2);
    if (feeds.length === 0) return;

    const placement = silhouetteLayout(feeds.length, width, height, this.layout);
    this.drawGroupBox(placement.groupBox);

    feeds.forEach((feed, i) => {
      const rect = placement.silhouettes[i];
      if (!rect) return;
      if (this.segmenter && this.segmenting) {
        this.drawSegmented(feed, rect);
      } else {
        this.ctx.drawImage(feed.video, rect.x, rect.y, rect.w, rect.h);
      }
      const lab = placement.labels[i];
      if (lab) this.drawName(feed.name, lab, placement.fontPx);
    });
  }

  /** The "theater balcony" frame around the whole group. */
  private drawGroupBox(box: Rect): void {
    const ctx = this.ctx;
    const r = Math.min(16, box.h * 0.12);
    ctx.save();
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") ctx.roundRect(box.x, box.y, box.w, box.h, r);
    else ctx.rect(box.x, box.y, box.w, box.h);
    ctx.fillStyle = "rgba(0,0,0,0.18)";
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.stroke();
    ctx.restore();
  }

  private drawName(name: string, lab: Label, fontPx: number): void {
    if (!name) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.font = `600 ${Math.round(fontPx)}px system-ui, -apple-system, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.shadowColor = "rgba(0,0,0,0.85)";
    ctx.shadowBlur = 4;
    ctx.fillStyle = "#fff";
    ctx.fillText(name, lab.x, lab.baseline, lab.maxWidth);
    ctx.restore();
  }

  private drawSegmented(feed: Feed, rect: { x: number; y: number; w: number; h: number }): void {
    const vw = feed.video.videoWidth;
    const vh = feed.video.videoHeight;
    if (!vw || !vh || !this.segmenter) {
      this.ctx.drawImage(feed.video, rect.x, rect.y, rect.w, rect.h);
      return;
    }
    const work = feed.work;
    work.width = vw;
    work.height = vh;
    const wctx = work.getContext("2d");
    if (!wctx) return;
    wctx.drawImage(feed.video, 0, 0, vw, vh);

    let masked = false;
    try {
      const result = this.segmenter.segmentForVideo(feed.video, performance.now());
      const mask = result.categoryMask;
      if (mask) {
        const cats = mask.getAsUint8Array();
        const frame = wctx.getImageData(0, 0, vw, vh);
        const px = frame.data;
        // MST3K silhouette: the person region (cats === 0 for this selfie model)
        // becomes solid black & opaque; everything else is transparent so the
        // movie shows through around the cutout.
        for (let i = 0; i < cats.length; i++) {
          const j = i * 4;
          if (cats[i] === 0) {
            px[j] = 0;
            px[j + 1] = 0;
            px[j + 2] = 0;
            px[j + 3] = 255;
          } else {
            px[j + 3] = 0;
          }
        }
        wctx.putImageData(frame, 0, 0);
        masked = true;
        this.segFailures = 0;
        mask.close();
      }
    } catch {
      // A transient glitch falls through to the raw draw below for this frame.
      // Only give up on segmentation after it fails persistently.
      if (++this.segFailures > 10) this.segmenting = false;
    }
    void masked;
    this.ctx.drawImage(work, rect.x, rect.y, rect.w, rect.h);
  }

  destroy(): void {
    this.stop();
    for (const id of [...this.feeds.keys()]) this.removePeer(id);
    this.segmenter?.close();
    this.segmenter = null;
  }
}
